import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, mergeProps as _mergeProps, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "contentHeader flex" }
const _hoisted_2 = { class: "flex" }
const _hoisted_3 = { class: "text-grey ml-2 mt-1.5" }
const _hoisted_4 = { class: "contentBox" }
const _hoisted_5 = { class: "block w-full" }
const _hoisted_6 = { class: "flex mb-4" }
const _hoisted_7 = ["disabled", "title"]
const _hoisted_8 = ["disabled", "title"]
const _hoisted_9 = {
  key: 0,
  class: "icon-pdf"
}
const _hoisted_10 = { class: "ml-auto" }
const _hoisted_11 = { class: "text-center" }

import {computed, onBeforeUnmount, onMounted, reactive, ref} from "vue";
import GridInputFilter from "@/components/GridInputFilter";
import {Menu, Store} from "@bryntum/grid";
import {BryntumGrid} from "@bryntum/grid-vue-3";
import {$t} from "@/utilities/i18n/config";
import Error from "@/components/Error";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {ErrorLocationType, PopupType, TableType} from '@/utilities/enums/Enums';
import {useGlobalStore} from "@/stores/GlobalStore";
import {DisdiInfo} from "@/models/disdi/DisdiInfo";
import {DisdiService} from "@/utilities/services/entities/DisdiService";
import {checkboxColumnConfig, defaultSpaceAroundTableOnPage, permissionColumn} from "@/AppConfig";
import {DisdiCsvExport} from "@/models/CsvExportModels";
import exportFromJSON from "export-from-json";
import router from "@/router";
import DateToStringHelper from "@/utilities/helpers/DateToStringHelper";
import FilterService from "@/utilities/services/FilterService";
import NullOrUndefinedHelper from "@/utilities/helpers/NullOrUndefinedHelper";


export default /*@__PURE__*/_defineComponent({
  __name: 'Disdi',
  setup(__props) {

const gridStore = new Store();
const globalStore = useGlobalStore();
const disdiService = new DisdiService();

let selectedDisdies = ref<Array<DisdiInfo>>([]);
const contextType = ref<TableType|null>(null);
const contextId = ref<number|null>(null);
const downloadingPdf = ref<boolean>(false);

const computedTitle = computed(() => {
  if (globalStore.registrationDate && globalStore.reportType === TableType[TableType.register]) {
    const subTitle = $t('disdi.view.subtitle', { date: globalStore.registrationDate});
    return $t('disdi.view.title', { subTitle, location: globalStore.selectedLocationName });
  } else {
    return $t('disdi.view.title', { subTitle: '', location: globalStore.selectedLocationName });
  }
});

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
  currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage}px`;
};

const exportCSV = () => {
  let data = selectedDisdies.value?.length > 0 ? selectedDisdies.value : gridStore.records;

  // remove unnecessary properties from csv
  //@ts-expect-error
  data = data.map((item)=> new DisdiCsvExport(item.data ?? item));

  const fileName = $t('disdi.view.csvExportFileName');
  const exportType =  exportFromJSON.types.csv;

  exportFromJSON({
    data:data,
    fileName:fileName,
    exportType:exportType,
  });
};

const getPdf = async () => {
  downloadingPdf.value = true;

  const disdiIds = selectedDisdies.value?.length > 0
      ? selectedDisdies.value.map(sd => sd.id)
      : gridStore.data.map(d => d.id);

  const success = await disdiService.exportPdf(disdiIds, ErrorLocationType.table);
  if(success) globalStore.showSuccess($t('disdi.view.pdfDownloaded'));

  downloadingPdf.value = false;
};


const grid = ref(null);
const useGridConfig = {
  headerMenuFeature: false,
  relayStoreEvents: true,
  enableTextSelection: true,
  selectionMode : {
    row	  : true,
    checkbox: checkboxColumnConfig,
    showCheckAll : true,
    deselectFilteredOutRecords: true
  },
  height: currentWindowHeight,
  columns : {
    data: [
      {
        id:'createDate',
        field: 'createDate',
        text: $t('disdi.view.grid.createDate'),
        flex: 1,
        type: "template",
        template:({value}:any)=>{
          return DateToStringHelper(new Date(value));
        }
      },
      {
        id: 'infoButton',
        field: 'infoButton',
        text: $t('disdi.view.grid.info'),
        width: 36,
        sortable: false,
        type: "template",
        template: () => {
          return `<div class='gridRowButton'><span class='icon-info'></span></div>`;
        },
      },
      {
        id:'diaryDescription',
        field: 'diaryDescription',
        text: $t('disdi.view.grid.diaryDescription'),
        flex: 1,
      },
      // {
      //   id:'comment',
      //   field: 'comment',
      //   text: $t('disdi.view.grid.comment'),
      //   flex: 1,
      // },
      {
        id:'userFullname',
        field: 'userFullname',
        text: $t('disdi.view.grid.userFullname'),
        flex: 1,
      },
      {
        id:'userPhoneNumber',
        field: 'userPhoneNumber',
        text: $t('disdi.view.grid.userPhoneNumber'),
        flex: 1,
      },
      {
        id:'companyName',
        field: 'companyName',
        text: $t('disdi.view.grid.companyName'),
        flex: 1,
      },
      {
        id:'categoryId',
        field: 'categoryId',
        text: $t('disdi.view.grid.categoryId'),
        flex: 1,
        type: "template",
        template: ({ record }: any) => {
            const category = record.data.categoryTitle;
            const iconSpan = `<span class="icon-${record.data.categoryIconName}"/>`;
            return NullOrUndefinedHelper(category) ? "" : `${category} (${iconSpan})`;
        }
      },
      {
        id:'registrationStart',
        field: 'registrationStart',
        text: $t('disdi.view.grid.registrationStart'),
        flex: 1,
        type: "template",
        template:({value}:any)=>{
          return value !== null ? DateToStringHelper(new Date(value)) : "";
        }
      },
      {
        id:'registrationEnd',
        field: 'registrationEnd',
        text: $t('disdi.view.grid.registrationEnd'),
        flex: 1,
        type: "template",
        template:({value}:any)=>{
          return value !== null ? DateToStringHelper(new Date(value)) : "";
        }
      },
      {
        id:'hasAttachment',
        field: 'hasAttachment',
        text: $t('disdi.view.grid.attachment'),
        type: "template",
        width   : 80,
        template:({record}:any)=>{
          return record.data.hasAttachment ? "<span class='icon-anhang'/>" : "";
        }
      },
      {
        id:'menu',
        text	: '',
        width   : 36,
        type	: 'widget',
        align: "center",
        hideable: false,
        resizable: false,
        sortable: false,
        filterable: false,
        widgets : [{
          type	 : 'button',
          width: 36,
          icon	 : 'icon-kontext_horizontal',
          onAction : ({ source: btn }) => {
            const menu = menuFactory(btn.cellInfo.record.data);
            menu.alignTo({ target: btn })
            menu.show();
          }
        }]
      },
    ],
    listeners:{
      async add(event:any){
        if(event.records.length === 1 && event.isMove[event.records[0].id]){
          await globalStore.saveUserColumnOrder(TableType[TableType.disdi], event.records[0].id, event.index.toString());
        }
      },
    }
  },
  store: gridStore,
  listeners: {
    selectionChange(event:any) {
      if(event.selection.length > 0 && event.selection[0]?.data === undefined) return;
      selectedDisdies.value = selectedDisdies.value.concat(event.selected.map(dr => dr.data))
      selectedDisdies.value = selectedDisdies.value.filter(d => !event.deselected.map(dr => dr.data).some(dr => d?.id === dr?.id));
    },
    datachange(){
      globalStore.currentItems = gridStore.count;
      globalStore.allItems = gridStore.originalCount;
    },
    async cellClick({record, column}:any){
      if(column.data.field === 'infoButton'){
        openDetailsPopup(record.data);
      }
    }
  }
};
const gridConfig = reactive(useGridConfig);

const menuFactory = (record:object) => {
  const menu = new Menu({
    anchor   : true,
    autoShow : true,
    items	: [
      {
        text: $t('disdi.view.details'),
        cls: 'color',
        weight: 100,
        onItem : ({ menu }:any) => {
          openDetailsPopup(menu.record);
        }
      },
    ],
  });

  //@ts-expect-error
  menu.setRecord(record);

  return menu;
};

const openDetailsPopup = (data:any)=>{
  globalStore.openPopup({
    data:data,
    action:globalStore.closeModal,
    resetButton:false,
    title:$t("popups.disdiDetails.title"),
    type:PopupType.disdiDetailsPopup
  });
}

const setContextProps = async ()=>{
  contextType.value = TableType[router.currentRoute.value.params.type as keyof typeof TableType];
  contextId.value = +router.currentRoute.value.params.id;
}

const setGridData = async ()=>{
  // start spinner
  globalStore.gridDataIsLoading = true;

  let disdies = contextType.value === TableType.register
      ? await disdiService.getDisdiEntriesForRegistration(contextId.value as number, ErrorLocationType.table)
      : await disdiService.getDisdiEntriesForLocation(contextId.value as number, ErrorLocationType.table);
  if(disdies === null) return;

  // add searchable categoryString just for this view
  disdies = disdies.map(d => {
    //@ts-expect-error
      d.categorySearchString = d.categoryTitle;
    return d;
  })

  

  //@ts-expect-error
  gridStore.setStoreData(disdies);
  globalStore.reorderColumns(TableType[TableType.disdi]);
  gridStore.sort("createDate", false); // always show newes disdi first

  // apply filters
  gridStore.filter({
    id: "userFilter",
    filterBy: FilterService.buildFilter(TableType[TableType.disdi])
  });

  // apply hidden cols
  for(const c of grid.value.instance.value.columns.storage.values){
    const needsHiding = globalStore.userPreferences.userHiddenColumns
        .some((thc) => thc.attributeName === c.data.field && thc.tableName === TableType[TableType.disdi]);

    if(needsHiding && !c.hidden) {
      c.hide();
    }
    else if(!needsHiding && c.hidden && !permissionColumn.includes(c.id)) {
      c.show();
    }
  }

  globalStore.closeModal();
  // stop spinner
  globalStore.gridDataIsLoading = false;
};

onMounted(async () => {
  // calculate grid height and sub to window resize event
  recalcGridHeight();
  window.addEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(ref(gridStore));
  globalStore.setGridColumnStore(ref(grid.value?.instance.value?.columns));

  await setContextProps();
  await setGridData();
})

onBeforeUnmount(()=>{
  window.removeEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(null);
  globalStore.setGridColumnStore(null);

  globalStore.currentItems = 0;
  globalStore.allItems = 0;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("button", {
        onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(router).back && _unref(router).back(...args)))
      }, [
        _createElementVNode("div", _hoisted_2, [
          _cache[1] || (_cache[1] = _createElementVNode("div", { class: "smallIcon icon-pfeil-links iconInButton" }, null, -1)),
          _createElementVNode("div", null, _toDisplayString(_unref($t)("global.back")), 1)
        ])
      ]),
      _createElementVNode("h4", _hoisted_3, _toDisplayString(computedTitle.value), 1)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _withDirectives(_createElementVNode("div", null, [
        _createVNode(_unref(Error), {
          errorLoc: _unref(ErrorLocationType).table
        }, null, 8, ["errorLoc"]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("button", {
              class: "primary mr-2",
              disabled: !_unref(selectedDisdies).some(()=>true),
              onClick: exportCSV,
              title: _unref($t)('global.exportCsv')
            }, _cache[2] || (_cache[2] = [
              _createElementVNode("span", { class: "icon-downlaod" }, null, -1)
            ]), 8, _hoisted_7),
            _createElementVNode("button", {
              class: "primary mr-2",
              disabled: !_unref(selectedDisdies).some(()=>true) || downloadingPdf.value,
              onClick: getPdf,
              title: _unref($t)('global.downloadPdf')
            }, [
              (!downloadingPdf.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_9))
                : (_openBlock(), _createBlock(LoadingSpinner, { key: 1 }))
            ], 8, _hoisted_8),
            _createElementVNode("div", _hoisted_10, [
              _createVNode(_unref(GridInputFilter), {
                gridStore: _unref(gridStore),
                popupType: _unref(PopupType).disdiFilterPopup
              }, null, 8, ["gridStore", "popupType"])
            ])
          ]),
          _createVNode(_unref(BryntumGrid), _mergeProps({
            ref_key: "grid",
            ref: grid
          }, gridConfig, { readOnly: true }), null, 16)
        ])
      ], 512), [
        [_vShow, !_unref(globalStore).gridDataIsLoading]
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_11, [
        _createVNode(LoadingSpinner)
      ], 512), [
        [_vShow, _unref(globalStore).gridDataIsLoading]
      ])
    ])
  ], 64))
}
}

})